import { resolveImageUrl } from "./assets";
import { PHASE_LABEL, type DeepDive } from "./deepDive";
import { cardShareUrl, withReferral } from "./engagement";
import { nativeShareFile } from "./native";
import { QR_GET_DATAURI } from "./qrGet";
import type { GameResult, InSnipCard } from "./types";

/**
 * Render a branded, shareable PNG of a card on an offscreen canvas, no external
 * dependency. Card images are self-hosted (same origin) so the canvas is not
 * tainted and toBlob works. Returns null if rendering isn't possible.
 */

const W = 1080;
const H = 1350;
const PAD = 84;
// Matches the "Obsidian & Aurora Gold" app theme.
const BG = "#0b0a0e";
const INK = "#f6f2ea";
const MUTED = "#a39db0";
const ACCENT = "#f0c668";

/** insnip.com (or current origin) without protocol, the readable brand link. */
function brandHost(): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://insnip.com";
  return origin.replace(/^https?:\/\//, "").replace(/\/+$/, "") || "insnip.com";
}

/** A clean, brand-only file slug: drops any internal generator prefix (e.g. "claude-",
 *  "draft-") from a card id so a shared file is "insnip-<hash>.png", never leaking the
 *  pipeline that authored it. */
function fileSlug(id: string): string {
  return (id || "").replace(/^[a-z][a-z0-9]*-/i, "") || "story";
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/** Draw an image cropped to "cover" a target rectangle. */
function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const scale = Math.max(w / img.width, h / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
}

/** Wrap text to a max width, returning at most maxLines (last line ellipsised). */
function wrapLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxLines: number,
): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      lines.push(line);
      line = word;
      if (lines.length === maxLines - 1) break;
    } else {
      line = candidate;
    }
  }
  if (lines.length < maxLines && line) lines.push(line);
  if (lines.length === maxLines) {
    // ellipsise the final line if there was overflow
    let last = lines[maxLines - 1];
    const consumed = lines.join(" ").split(/\s+/).length;
    if (consumed < words.length) {
      while (last && ctx.measureText(`${last}…`).width > maxWidth) {
        last = last.slice(0, -1).trimEnd();
      }
      lines[maxLines - 1] = `${last}…`;
    }
  }
  return lines;
}

const SERIF = '"Source Serif 4", Georgia, serif';
const SANS = 'Inter, ui-sans-serif, system-ui, sans-serif';

export async function renderCardImage(
  card: InSnipCard,
  opts: { shortUrl?: string } = {},
): Promise<Blob | null> {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // Best-effort: wait for the brand fonts so the PNG matches the app.
  try {
    await (document as Document & { fonts?: FontFaceSet }).fonts?.ready;
  } catch {
    /* fonts API unavailable, fall back to system fonts */
  }

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);

  // Top image band with a fade into the background.
  const bandH = 540;
  const img = await loadImage(resolveImageUrl(card.imageUrl));
  if (img) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, W, bandH);
    ctx.clip();
    drawCover(ctx, img, 0, 0, W, bandH);
    ctx.restore();
  }
  const fade = ctx.createLinearGradient(0, bandH - 260, 0, bandH);
  fade.addColorStop(0, "rgba(11,10,14,0)");
  fade.addColorStop(1, BG);
  ctx.fillStyle = fade;
  ctx.fillRect(0, bandH - 260, W, 260);

  // Atmospheric aurora-gold halo over the lower content area (subtle depth).
  const halo = ctx.createRadialGradient(W / 2, bandH + 40, 0, W / 2, bandH + 40, W * 0.8);
  halo.addColorStop(0, "rgba(240,198,104,0.06)");
  halo.addColorStop(1, "rgba(11,10,14,0)");
  ctx.fillStyle = halo;
  ctx.fillRect(0, bandH - 60, W, H - bandH + 60);

  let y = bandH + 34;

  // Category eyebrow.
  const category = (card.category ?? "InSnip").toString().toUpperCase();
  ctx.font = `700 24px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.textBaseline = "top";
  ctx.fillText(category.slice(0, 40), PAD, y, W - PAD * 2);
  y += 46;

  // Headline.
  ctx.font = `700 54px ${SERIF}`;
  ctx.fillStyle = INK;
  for (const line of wrapLines(ctx, card.headline, W - PAD * 2, 4)) {
    ctx.fillText(line, PAD, y);
    y += 66;
  }
  y += 12;

  // Essence.
  ctx.font = `400 31px ${SERIF}`;
  ctx.fillStyle = MUTED;
  for (const line of wrapLines(ctx, card.essence ?? "", W - PAD * 2, 3)) {
    ctx.fillText(line, PAD, y);
    y += 44;
  }

  // Why it matters, gold-bordered block, mirroring the in-app why-box.
  const why = (card.whyItMatters ?? "").trim();
  if (why) {
    y += 22;
    ctx.font = `400 28px ${SERIF}`;
    const whyLines = wrapLines(ctx, why, W - PAD * 2 - 36, 3);
    const blockTop = y;
    const blockH = 30 + 40 + whyLines.length * 40 + 28;
    ctx.fillStyle = "rgba(240,198,104,0.07)";
    roundRect(ctx, PAD, blockTop, W - PAD * 2, blockH, 18);
    ctx.fill();
    ctx.fillStyle = ACCENT;
    ctx.fillRect(PAD, blockTop, 5, blockH); // gold left spine
    let wy = blockTop + 26;
    ctx.font = `700 19px ${SANS}`;
    ctx.fillStyle = ACCENT;
    ctx.fillText("WHY IT MATTERS", PAD + 36, wy);
    wy += 42;
    ctx.font = `italic 400 29px ${SERIF}`;
    ctx.fillStyle = INK;
    for (const line of whyLines) {
      ctx.fillText(line, PAD + 36, wy);
      wy += 40;
    }
  }

  // Footer band: brand + an explicit "get the app" call on the left, and a QR on the right
  // that routes any scanner to the download page (Play now, App Store soon). Every shared
  // screenshot then carries its own install path, not just a link that only works if tapped.
  const qrSize = 156;
  const qrPad = 15;
  const plate = qrSize + qrPad * 2;
  const plateX = W - PAD - plate;
  const plateY = H - PAD - plate;

  // Clean footer base: fade any overflowing content into the background so the QR + CTA always
  // sit on a solid band, no matter how long the headline/essence/why ran above.
  const bandTop = plateY - 46;
  const bandFade = ctx.createLinearGradient(0, bandTop, 0, bandTop + 64);
  bandFade.addColorStop(0, "rgba(11,10,14,0)");
  bandFade.addColorStop(1, BG);
  ctx.fillStyle = bandFade;
  ctx.fillRect(0, bandTop, W, 64);
  ctx.fillStyle = BG;
  ctx.fillRect(0, bandTop + 64, W, H - bandTop - 64);

  // White rounded plate so the QR always has the quiet zone and contrast it needs to scan.
  ctx.fillStyle = "#ffffff";
  roundRect(ctx, plateX, plateY, plate, plate, 20);
  ctx.fill();
  const qrImg = await loadImage(QR_GET_DATAURI);
  if (qrImg) ctx.drawImage(qrImg, plateX + qrPad, plateY + qrPad, qrSize, qrSize);

  // "SCAN TO GET THE APP" eyebrow centered above the plate.
  ctx.font = `800 19px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.textAlign = "center";
  ctx.fillText("SCAN TO GET THE APP", plateX + plate / 2, plateY - 28);
  ctx.textAlign = "left";

  // Left column: gold rule, wordmark, the Play call to action, and the short link.
  const leftMax = plateX - PAD - 28;
  let fy = plateY + 4;
  ctx.fillStyle = ACCENT;
  ctx.fillRect(PAD, fy, 64, 5);
  fy += 26;
  ctx.font = `800 40px ${SANS}`;
  ctx.fillStyle = INK;
  ctx.fillText("InSnip", PAD, fy);
  fy += 52;
  ctx.font = `700 26px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.fillText("Get it free on Google Play", PAD, fy, leftMax);
  fy += 40;
  ctx.font = `500 23px ${SANS}`;
  ctx.fillStyle = MUTED;
  const displayLink = opts.shortUrl ? opts.shortUrl.replace(/^https?:\/\//, "") : brandHost();
  ctx.fillText(displayLink, PAD, fy, leftMax);

  // JPEG (not PNG): the card is a photo-backed gradient, so JPEG at 0.9 is visually identical
  // but roughly a fifth of the size (~200KB vs 1MB+), so sharing is fast on mobile data.
  return await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9),
  );
}

// ── Shareable daily recap ("My InSnip day"), the pride moment becomes a growth loop ──
export type RecapShare = {
  streak: number;
  cardsRead: number;
  topCategory: string | null;
  rounds: number;
};

/** Render a branded, shareable PNG of the reader's day: streak hero + the day's stats.
 *  Same offscreen-canvas approach + theme as renderCardImage. null if not possible. */
export async function renderRecapImage(r: RecapShare): Promise<Blob | null> {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  try {
    await (document as Document & { fonts?: FontFaceSet }).fonts?.ready;
  } catch {
    /* fonts API unavailable, system fonts */
  }

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);
  // Aurora-gold halo behind the hero number (depth + warmth).
  const halo = ctx.createRadialGradient(W / 2, 470, 0, W / 2, 470, W * 0.85);
  halo.addColorStop(0, "rgba(240,198,104,0.16)");
  halo.addColorStop(1, "rgba(11,10,14,0)");
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, W, H);

  ctx.textBaseline = "top";
  ctx.textAlign = "center";
  const date = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
  ctx.font = `800 28px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.fillText("MY INSNIP DAY", W / 2, 150);
  ctx.font = `400 27px ${SANS}`;
  ctx.fillStyle = MUTED;
  ctx.fillText(date, W / 2, 196);

  // Hero: the streak flame + big number (the emotional centrepiece), else cards read.
  const hasStreak = r.streak > 0;
  ctx.font = `400 120px ${SANS}`;
  ctx.fillText(hasStreak ? "🔥" : "📖", W / 2, 282);
  ctx.font = `800 230px ${SANS}`;
  ctx.fillStyle = INK;
  ctx.fillText(String(hasStreak ? r.streak : r.cardsRead), W / 2, 408);
  ctx.font = `700 42px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.fillText(hasStreak ? "day streak" : "cards read today", W / 2, 672);

  // Stat rows.
  const stats: [string, string][] = [
    ["📖 Cards read", String(r.cardsRead)],
    ...(r.topCategory ? ([["📚 Top topic", r.topCategory]] as [string, string][]) : []),
    ["⏱️ Time saved", `~${r.cardsRead * 3} min`],
  ];
  let sy = 770;
  for (const [label, val] of stats) {
    ctx.fillStyle = "rgba(255,255,255,0.05)";
    roundRect(ctx, PAD, sy, W - PAD * 2, 96, 18);
    ctx.fill();
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.font = `500 33px ${SANS}`;
    ctx.fillStyle = MUTED;
    ctx.fillText(label, PAD + 38, sy + 50, W - PAD * 2 - 260);
    ctx.textAlign = "right";
    ctx.font = `800 37px ${SANS}`;
    ctx.fillStyle = INK;
    ctx.fillText(val, W - PAD - 38, sy + 50, 320);
    ctx.textBaseline = "top";
    sy += 116;
  }

  // Footer: gold rule + wordmark + readable insnip.com link.
  const footerY = H - 92;
  ctx.fillStyle = ACCENT;
  ctx.fillRect(PAD, footerY - 26, 64, 5);
  ctx.textAlign = "left";
  ctx.font = `800 34px ${SANS}`;
  ctx.fillStyle = INK;
  ctx.fillText("InSnip", PAD, footerY);
  ctx.textAlign = "right";
  ctx.font = `600 26px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.fillText(brandHost(), W - PAD, footerY + 4);

  return await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9),
  );
}

/** Share the daily recap as an image (native share sheet, else download). */
export async function shareRecapImage(
  r: RecapShare,
): Promise<"shared" | "downloaded" | "failed"> {
  const blob = await renderRecapImage(r);
  if (!blob) return "failed";
  const fileName = "insnip-my-day.jpg";
  const origin = typeof window !== "undefined" ? window.location.origin : "https://insnip.com";
  const link = origin.replace(/\/+$/, "");
  const text =
    r.streak > 0
      ? `🔥 ${r.streak}-day streak on InSnip, ${r.cardsRead} stories today, all in a few minutes. ${link}`
      : `${r.cardsRead} stories in a few minutes on InSnip. ${link}`;

  if (await nativeShareFile(blob, fileName, "My InSnip day", text)) return "shared";
  const file = new File([blob], fileName, { type: "image/jpeg" });
  const nav = navigator as Navigator & { canShare?: (data: { files: File[] }) => boolean };
  if (nav.canShare?.({ files: [file] }) && navigator.share) {
    try {
      await navigator.share({ files: [file], title: "My InSnip day", text });
      return "shared";
    } catch {
      return "failed";
    }
  }
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return "downloaded";
  } catch {
    return "failed";
  }
}

// ── Shareable Signal or Noise result, the daily game's brag becomes a growth loop ──

// The game's dark-theme verdict colours (styles.css --green / --son-red), baked in so the
// PNG matches the in-app reveal without depending on the DOM.
const GAME_GREEN = "#7fdcb2";
const GAME_RED = "#ef6a6a";

/** Render a branded, shareable PNG of a Signal or Noise result: wordmark, eyebrow, the huge
 *  score in the display serif, the share grid as coloured rounded squares (never emoji
 *  glyphs, canvas emoji rendering is inconsistent), streak and date. Same offscreen-canvas
 *  approach + theme as renderRecapImage. null if not possible. */
export async function renderGameImage(
  day: string,
  result: GameResult,
  streak: number,
): Promise<Blob | null> {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  try {
    await (document as Document & { fonts?: FontFaceSet }).fonts?.ready;
  } catch {
    /* fonts API unavailable, system fonts */
  }

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);
  // Aurora-gold halo behind the hero score (depth + warmth).
  const halo = ctx.createRadialGradient(W / 2, 560, 0, W / 2, 560, W * 0.85);
  halo.addColorStop(0, "rgba(240,198,104,0.14)");
  halo.addColorStop(1, "rgba(11,10,14,0)");
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, W, H);

  ctx.textBaseline = "top";
  ctx.textAlign = "center";

  // Brand wordmark up top, over a short gold rule.
  ctx.fillStyle = ACCENT;
  ctx.fillRect(W / 2 - 32, 116, 64, 5);
  ctx.font = `800 40px ${SANS}`;
  ctx.fillStyle = INK;
  ctx.fillText("InSnip", W / 2, 146);

  // Eyebrow + date.
  ctx.font = `800 30px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.fillText("SIGNAL OR NOISE", W / 2, 252);
  const parsed = new Date(`${day}T00:00:00`);
  const dateLabel = Number.isNaN(parsed.getTime())
    ? day
    : parsed.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  ctx.font = `400 27px ${SANS}`;
  ctx.fillStyle = MUTED;
  ctx.fillText(dateLabel, W / 2, 300);

  // Hero: the huge score in the display serif, "4" gold with "/ 6" trailing muted.
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.font = `700 250px ${SERIF}`;
  const bigW = ctx.measureText(String(result.correct)).width;
  ctx.font = `700 110px ${SERIF}`;
  const smallW = ctx.measureText(` / ${result.total}`).width;
  const scoreX = (W - bigW - smallW) / 2;
  const scoreBase = 640;
  ctx.font = `700 250px ${SERIF}`;
  ctx.fillStyle = ACCENT;
  ctx.fillText(String(result.correct), scoreX, scoreBase);
  ctx.font = `700 110px ${SERIF}`;
  ctx.fillStyle = MUTED;
  ctx.fillText(` / ${result.total}`, scoreX + bigW, scoreBase);
  ctx.textBaseline = "top";

  // The share grid as coloured rounded squares: green = called right, red = fooled.
  const marks: boolean[] = result.items.length
    ? result.items.map((it) => it.right)
    : Array.from({ length: result.total }, (_, i) => i < result.correct);
  const sq = 84;
  const gap = 20;
  const perRow = 6;
  let gy = 730;
  for (let row = 0; row < marks.length; row += perRow) {
    const rowMarks = marks.slice(row, row + perRow);
    let gx = (W - (rowMarks.length * sq + (rowMarks.length - 1) * gap)) / 2;
    for (const right of rowMarks) {
      ctx.fillStyle = right ? GAME_GREEN : GAME_RED;
      roundRect(ctx, gx, gy, sq, sq, 20);
      ctx.fill();
      gx += sq + gap;
    }
    gy += sq + gap;
  }

  // Streak flame + count (only once a streak exists).
  if (streak > 0) {
    ctx.textAlign = "center";
    ctx.font = `400 44px ${SANS}`;
    ctx.fillText("🔥", W / 2, gy + 46);
    ctx.font = `700 40px ${SANS}`;
    ctx.fillStyle = ACCENT;
    ctx.fillText(`${streak} day streak`, W / 2, gy + 106);
    ctx.textAlign = "left";
  }

  // Footer: gold rule + the challenge line + the referral link.
  const footerY = H - 104;
  ctx.textAlign = "left";
  ctx.fillStyle = ACCENT;
  ctx.fillRect(PAD, footerY - 26, 64, 5);
  ctx.font = `700 30px ${SANS}`;
  ctx.fillStyle = INK;
  ctx.fillText("Can you tell what's real?", PAD, footerY);
  ctx.font = `500 23px ${SANS}`;
  ctx.fillStyle = MUTED;
  ctx.fillText("Play today's round on InSnip", PAD, footerY + 46);
  ctx.font = `600 26px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.textAlign = "right";
  ctx.fillText(withReferral(`${brandHost()}/`), W - PAD, footerY + 4, W * 0.55);
  ctx.textAlign = "left";

  return await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9),
  );
}

/** Share a Signal or Noise result as an image (native share sheet, else download). The game
 *  shares the referral URL directly (matching its text share), never a minted short link. */
export async function shareGameImage(
  day: string,
  result: GameResult,
  streak: number,
): Promise<"shared" | "downloaded" | "failed"> {
  const blob = await renderGameImage(day, result, streak);
  if (!blob) return "failed";
  const fileName = `insnip-signal-or-noise-${day}.jpg`;
  const text =
    `Signal or Noise · ${day}\n` +
    `${result.shareGrid}\n` +
    `${result.correct}/${result.total} · streak ${streak} 🔥\n` +
    `Can you tell what's real? ${withReferral("https://insnip.com/")}`;

  if (await nativeShareFile(blob, fileName, "Signal or Noise", text)) return "shared";
  const file = new File([blob], fileName, { type: "image/jpeg" });
  const nav = navigator as Navigator & { canShare?: (data: { files: File[] }) => boolean };
  if (nav.canShare?.({ files: [file] }) && navigator.share) {
    try {
      await navigator.share({ files: [file], title: "Signal or Noise", text });
      return "shared";
    } catch {
      return "failed";
    }
  }
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return "downloaded";
  } catch {
    return "failed";
  }
}

/**
 * Render a branded, shareable PNG of a card's DEEP DIVE: the story's flow as a numbered gold
 * thread (the throughline + the typed beats), so a shared image sells the click by showing HOW a
 * story unfolds, not just its headline. Portrait 1080x1350, Obsidian/Aurora-Gold theme, offscreen
 * canvas (no external dependency). null if rendering isn't possible.
 */
export async function renderDeepDiveImage(
  card: InSnipCard,
  deepDive: DeepDive,
  opts: { shortUrl?: string } = {},
): Promise<Blob | null> {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  try {
    await (document as Document & { fonts?: FontFaceSet }).fonts?.ready;
  } catch {
    /* system fonts */
  }

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, W, H);
  const halo = ctx.createRadialGradient(W * 0.15, 300, 0, W * 0.15, 300, W * 0.9);
  halo.addColorStop(0, "rgba(240,198,104,0.12)");
  halo.addColorStop(1, "rgba(11,10,14,0)");
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, W, H);

  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  let y = 108;

  // Eyebrow: DEEP DIVE . CATEGORY
  const eyebrow = `DEEP DIVE   ·   ${(card.category ?? "InSnip").toString().toUpperCase()}`;
  ctx.font = `800 24px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.fillText(eyebrow.slice(0, 46), PAD, y, W - PAD * 2);
  y += 46;

  // Headline (serif), up to 3 lines.
  ctx.font = `700 52px ${SERIF}`;
  ctx.fillStyle = INK;
  for (const line of wrapLines(ctx, card.headline, W - PAD * 2, 3)) {
    ctx.fillText(line, PAD, y);
    y += 62;
  }
  y += 8;

  // Throughline (the takeaway), italic serif, up to 3 lines.
  const takeaway = (deepDive.takeaway ?? card.essence ?? "").trim();
  if (takeaway) {
    ctx.font = `italic 400 28px ${SERIF}`;
    ctx.fillStyle = MUTED;
    for (const line of wrapLines(ctx, takeaway, W - PAD * 2, 3)) {
      ctx.fillText(line, PAD, y);
      y += 40;
    }
  }
  y += 18;

  // Gold divider.
  ctx.fillStyle = ACCENT;
  ctx.fillRect(PAD, y, 64, 4);
  y += 30;

  // The thread: numbered gold nodes on a spine + phase label + heading + one detail line.
  const beats = deepDive.beats.slice(0, 5);
  const nodeX = PAD + 24;
  const textX = PAD + 78;
  const textW = W - textX - PAD;
  const threadTop = y;
  const footerTop = H - 150;
  const block = Math.max(120, Math.min(178, (footerTop - threadTop) / beats.length));
  const headingLines = block >= 152 ? 2 : 1;
  const showDetail = block >= 132;

  // Spine behind the nodes.
  ctx.strokeStyle = "rgba(240,198,104,0.35)";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(nodeX, threadTop + 24);
  ctx.lineTo(nodeX, threadTop + (beats.length - 1) * block + 24);
  ctx.stroke();

  beats.forEach((b, i) => {
    const top = threadTop + i * block;
    const cy = top + 24;
    // node circle with the beat number
    ctx.beginPath();
    ctx.arc(nodeX, cy, 21, 0, Math.PI * 2);
    ctx.fillStyle = ACCENT;
    ctx.fill();
    ctx.fillStyle = BG;
    ctx.font = `800 22px ${SANS}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(String(i + 1), nodeX, cy + 1);
    ctx.textAlign = "left";
    ctx.textBaseline = "top";

    // phase label
    ctx.font = `800 18px ${SANS}`;
    ctx.fillStyle = ACCENT;
    ctx.fillText(PHASE_LABEL[b.phase].toUpperCase(), textX, top - 2, textW);
    // heading
    ctx.font = `700 30px ${SERIF}`;
    ctx.fillStyle = INK;
    let hy = top + 24;
    for (const line of wrapLines(ctx, b.heading, textW, headingLines)) {
      ctx.fillText(line, textX, hy);
      hy += 36;
    }
    // one detail line
    if (showDetail) {
      ctx.font = `400 23px ${SERIF}`;
      ctx.fillStyle = MUTED;
      const detail = wrapLines(ctx, b.line, textW, 1)[0] ?? "";
      ctx.fillText(detail, textX, hy + 2, textW);
    }
  });

  // Footer: gold rule + wordmark + sell line + short link.
  const fY = H - 104;
  ctx.fillStyle = ACCENT;
  ctx.fillRect(PAD, fY - 26, 64, 5);
  ctx.font = `800 34px ${SANS}`;
  ctx.fillStyle = INK;
  ctx.fillText("InSnip", PAD, fY);
  ctx.font = `500 23px ${SANS}`;
  ctx.fillStyle = MUTED;
  ctx.fillText("See how the story flows", PAD, fY + 44);
  ctx.font = `600 26px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.textAlign = "right";
  const link = opts.shortUrl ? opts.shortUrl.replace(/^https?:\/\//, "") : brandHost();
  ctx.fillText(link, W - PAD, fY + 4, W * 0.55);
  ctx.textAlign = "left";

  return await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9),
  );
}

/** Share a card's deep dive as an image (native share sheet, else download). */
export async function shareDeepDiveImage(
  card: InSnipCard,
  deepDive: DeepDive,
): Promise<"shared" | "downloaded" | "failed"> {
  const link = await cardShareUrl(card.id);
  const blob = await renderDeepDiveImage(card, deepDive, { shortUrl: link });
  if (!blob) return "failed";
  const fileName = `insnip-deepdive-${fileSlug(card.id)}.jpg`;
  const shareText = `${card.headline}\n\nHow the story flows, a quick Deep Dive on InSnip:\n${link}`;

  if (await nativeShareFile(blob, fileName, card.headline, shareText)) return "shared";
  const file = new File([blob], fileName, { type: "image/jpeg" });
  const nav = navigator as Navigator & { canShare?: (data: { files: File[] }) => boolean };
  if (nav.canShare?.({ files: [file] }) && navigator.share) {
    try {
      await navigator.share({ files: [file], title: card.headline, text: shareText });
      return "shared";
    } catch {
      return "failed";
    }
  }
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return "downloaded";
  } catch {
    return "failed";
  }
}

/** The brand canvas toolkit, exported once so newer share renderers (v3: proof, story,
 *  archetype) compose the exact same look without duplicating these helpers. */
export const shareKit = {
  W,
  H,
  PAD,
  BG,
  INK,
  MUTED,
  ACCENT,
  SERIF,
  SANS,
  brandHost,
  fileSlug,
  loadImage,
  drawCover,
  wrapLines,
  roundRect,
} as const;

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

const SQUARE = 1080;

/**
 * Render a square (1080x1080) shareable PNG of a single pull-quote from a card, in the
 * app's Obsidian/Aurora-Gold theme: oversized gold quotation mark, the quote in serif, a
 * category eyebrow, the wordmark + motto, and the short share link. Square is the most
 * universally shareable shape (Instagram, X, WhatsApp). null if rendering isn't possible.
 */
export async function renderQuoteImage(
  card: InSnipCard,
  quote: string,
  opts: { shortUrl?: string } = {},
): Promise<Blob | null> {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = SQUARE;
  canvas.height = SQUARE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  try {
    await (document as Document & { fonts?: FontFaceSet }).fonts?.ready;
  } catch {
    /* system fonts */
  }

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, SQUARE, SQUARE);
  const halo = ctx.createRadialGradient(SQUARE / 2, SQUARE * 0.34, 0, SQUARE / 2, SQUARE * 0.34, SQUARE * 0.78);
  halo.addColorStop(0, "rgba(240,198,104,0.10)");
  halo.addColorStop(1, "rgba(11,10,14,0)");
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, SQUARE, SQUARE);

  const pad = 96;
  ctx.textBaseline = "top";
  ctx.textAlign = "left";

  // Category eyebrow.
  ctx.font = `700 26px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.fillText((card.category ?? "InSnip").toString().toUpperCase().slice(0, 40), pad, 112);

  // Oversized opening quotation mark.
  ctx.font = `700 210px ${SERIF}`;
  ctx.fillStyle = "rgba(240,198,104,0.85)";
  ctx.fillText("“", pad - 10, 120);

  // Quote text, shrunk to fit between the mark and the footer.
  const maxW = SQUARE - pad * 2;
  const topY = 312;
  const bottomLimit = SQUARE - 230;
  let size = 66;
  let lines: string[] = [];
  for (; size >= 34; size -= 4) {
    ctx.font = `600 ${size}px ${SERIF}`;
    lines = wrapLines(ctx, quote, maxW, 9);
    if (lines.length * size * 1.3 <= bottomLimit - topY) break;
  }
  ctx.font = `600 ${size}px ${SERIF}`;
  ctx.fillStyle = INK;
  let qy = topY;
  for (const line of lines) {
    ctx.fillText(line, pad, qy);
    qy += size * 1.3;
  }

  // Footer: gold rule + wordmark + motto + short link.
  const footerY = SQUARE - 150;
  ctx.fillStyle = ACCENT;
  ctx.fillRect(pad, footerY - 24, 64, 5);
  ctx.font = `800 36px ${SANS}`;
  ctx.fillStyle = INK;
  ctx.fillText("InSnip", pad, footerY);
  ctx.font = `italic 400 24px ${SERIF}`;
  ctx.fillStyle = MUTED;
  ctx.fillText("Pure Signal, No Noise", pad, footerY + 48);

  ctx.font = `600 26px ${SANS}`;
  ctx.fillStyle = ACCENT;
  ctx.textAlign = "right";
  const quoteLink = opts.shortUrl ? opts.shortUrl.replace(/^https?:\/\//, "") : brandHost();
  ctx.fillText(quoteLink, SQUARE - pad, footerY + 6, SQUARE * 0.55);
  ctx.textAlign = "left";

  return await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.9),
  );
}

/** Share a single quote from a card as a square image (native share, else download). */
export async function shareQuoteImage(
  card: InSnipCard,
  quote: string,
): Promise<"shared" | "downloaded" | "failed"> {
  const shortUrl = await cardShareUrl(card.id);
  const blob = await renderQuoteImage(card, quote, { shortUrl });
  if (!blob) return "failed";
  const fileName = `insnip-quote-${fileSlug(card.id)}.jpg`;
  const shareText = `“${quote}”\n\nvia InSnip:\n${shortUrl}`;

  if (await nativeShareFile(blob, fileName, card.headline, shareText)) return "shared";
  const file = new File([blob], fileName, { type: "image/jpeg" });
  const nav = navigator as Navigator & { canShare?: (data: { files: File[] }) => boolean };
  if (nav.canShare?.({ files: [file] }) && navigator.share) {
    try {
      await navigator.share({ files: [file], title: card.headline, text: shareText });
      return "shared";
    } catch {
      return "failed";
    }
  }
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return "downloaded";
  } catch {
    return "failed";
  }
}

/**
 * Share a card as an image: native file share where supported (mobile), else
 * trigger a download. Returns "shared" | "downloaded" | "failed".
 */
export async function shareCardImage(
  card: InSnipCard,
): Promise<"shared" | "downloaded" | "failed"> {
  // Mint the short link FIRST so it can be baked onto the image too ("sell the click").
  // Prefer insnip.com/s/<code> (clean, short, carries referral); fall back to the /c/<id>
  // permalink if minting is slow or fails, so sharing is never blocked. The link goes in
  // the caption ONCE and is NOT also passed as `url` (WhatsApp etc. append url to text).
  const link = await cardShareUrl(card.id);
  const blob = await renderCardImage(card, { shortUrl: link });
  if (!blob) return "failed";
  const fileName = `insnip-${fileSlug(card.id)}.jpg`;
  const shareText = `${card.headline}\n\nRead it free on InSnip, the app for quick, source-checked news:\n${link}`;

  // Native iOS/Android share sheet first (Capacitor), then web. No `url` (the link is in the text).
  if (await nativeShareFile(blob, fileName, card.headline, shareText)) return "shared";

  const file = new File([blob], fileName, { type: "image/jpeg" });

  const nav = navigator as Navigator & {
    canShare?: (data: { files: File[] }) => boolean;
  };
  if (nav.canShare?.({ files: [file] }) && navigator.share) {
    try {
      await navigator.share({ files: [file], title: card.headline, text: shareText });
      return "shared";
    } catch {
      return "failed"; // user cancelled
    }
  }

  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return "downloaded";
  } catch {
    return "failed";
  }
}
